import Sentry from '@/services/sentry'
import { nextTick } from 'vue'
import PresentationApi from '@/api/presentation'
import { scrollToActiveSlide } from '@/utils/dom.utils'
import EventBus from '@/eventBus'
import {
  processCanvasBlocksForSlides,
  determineActiveSlide,
  preserveAnnotationProperties,
  validatePresentationId,
  identifyNewSlides,
} from '@/business-logic/data-third-party-action'
import { trackChatbotSlideCreation } from '@/utils/chatbot-slide-tracking.utils'
import { trackImportSuccessful } from '@/vuex/modules/ai-chatbot/ai-chatbot-import-pptx-pdf.module'
import router from '@/router'
import { recallImportInfo } from '@/utils/importTracking'
import { EVENT_CATEGORIES_BY_ROUTE_NAME } from '@/constant/routes'

/**
 * Updates presentation state with new slides data while preserving annotation properties
 * @param {Function} dispatch - Vuex dispatch function
 * @param {Object} rootState - Vuex root state to get latest slides
 * @param {Array} processedSlides - Slides with canvas blocks processed
 */
const updatePresentationState = async (dispatch, rootState, processedSlides) => {
  await dispatch('setIsNewPresentationAction', false, { root: true })

  // Get the latest slides from store to ensure we have most recent annotation data
  const latestSlides = rootState.presentation?.slides || []

  // Preserve annotation properties from latest slides (not stale originalSlides)
  const slidesWithPreservedAnnotations = preserveAnnotationProperties(processedSlides, latestSlides)

  await dispatch('onlyPatchPresentationAction', { slides: slidesWithPreservedAnnotations }, { root: true })
}

/**
 * Sets the active slide in the store
 * @param {Function} dispatch - Vuex dispatch function
 * @param {Object} slideToActivate - The slide to set as active
 */
const setActiveSlideInStore = async (dispatch, slideToActivate) => {
  await dispatch('setSlideActive', slideToActivate, { root: true })
  const slideType = slideToActivate?.slideType || slideToActivate?.type

  // Emit content sync events for supported slide types
  if (slideToActivate && slideType) {
    await dispatch('slideContentSync/handleSlideContentChange', slideToActivate, { root: true })
  }
}

/**
 * Emits slide update event to the event bus
 * @param {Object} slideToActivate - The slide that was activated
 */
const emitSlideUpdateEvent = (slideToActivate) => {
  EventBus.$emit('updateNextSlide', slideToActivate.id)
}

/**
 * Clears annotations for the activated slide
 * @param {Function} dispatch - Vuex dispatch function
 * @param {Object} slideToActivate - The slide to clear annotations for
 */
const clearSlideAnnotations = async (dispatch, slideToActivate) => {
  await dispatch('slideAnnotations/clearAnnotationAction', slideToActivate.id, { root: true })
}

/**
 * Sets the active slide and handles related side effects
 * @param {Function} dispatch - Vuex dispatch function
 * @param {Object|null} slideToActivate - The slide to set as active
 * @param {Object} [rootGetters] - Vuex root getters, read for the canvas background refresh flag
 */
const setActiveSlideIfNeeded = async (dispatch, slideToActivate, rootGetters) => {
  if (!slideToActivate) {
    return
  }

  // Set the active slide in the store
  await setActiveSlideInStore(dispatch, slideToActivate)

  if (rootGetters?.['featureToggles/canUseAiAddedSlideBackgroundRefresh']) {
    EventBus.$emit('eventHandleSetValueTransition')
  }

  // Emit event for slide update
  emitSlideUpdateEvent(slideToActivate)

  // Clear annotations for the new active slide
  await clearSlideAnnotations(dispatch, slideToActivate)
}

/**
 * Handles DOM updates after slide activation
 */
const performDOMUpdates = async () => {
  await nextTick()
  scrollToActiveSlide()
}

/**
 * Tracks import success before clearing state
 * @param {Object} rootGetters - Vuex root getters to access uploaded file and import option
 */
const trackImportSuccessBeforeClearingState = (rootGetters, presentationId) => {
  const uploadedFile = rootGetters['aiChatbotImportPptxPdf/aiChatbotUploadedFile']
  const selectedImportOption = rootGetters['aiChatbotImportPptxPdf/selectedImportOption']

  // "Import and generate slides with AI" nulls the uploaded file the moment it
  // posts the message to the chatbot-app, and this runs minutes later — so on
  // that path everything below reads undefined and the event ships describing
  // no file at all. Fall back to the snapshot taken at hand-off.
  //
  // Only when there is nothing live to read: an import that still has its file
  // is the more current truth, and the snapshot is dropped as soon as the next
  // import starts on this deck.
  if (!uploadedFile) {
    const remembered = recallImportInfo(presentationId)
    if (remembered) {
      trackImportSuccessful({
        ...remembered,
        eventCategory: EVENT_CATEGORIES_BY_ROUTE_NAME[router?.currentRoute?.name],
      }, presentationId)
      return
    }
  }

  trackImportSuccessful({
    file_name: uploadedFile?.file_metadata?.original_filename,
    file_type: uploadedFile?.file_metadata?.content_type,
    file_metadata: uploadedFile?.file_metadata,
    import_option: selectedImportOption,
    provider: uploadedFile?.import_provider,
    source: uploadedFile?.source,
    eventCategory: EVENT_CATEGORIES_BY_ROUTE_NAME[router?.currentRoute?.name],
  }, presentationId)
}

/**
 * Handles the final UI updates after presentation changes
 * @param {Function} dispatch - Vuex dispatch function
 * @param {Object|null} slideToActivate - The slide to set as active
 * @param {Object} rootGetters
 * @param {boolean} importInFlight - Whether a v2 import is mid-run on THIS
 *   presentation. Sampled by the caller before its awaits; see below.
 * @param {boolean} activeSlideGone - The slide on screen no longer exists.
 */
const handleUIUpdates = async (
  dispatch, slideToActivate, rootGetters, importInFlight, presentationId, activeSlideGone = false,
) => {
  // AHA-47691: on backstage or while presenting, the slide on screen is the one the
  // presenter is running, so a refresh must not move it. A slide the assistant adds or
  // edits shows up in the deck and the presenter goes to it when ready.
  const holdsActiveSlide = Boolean(rootGetters?.isBackstage || rootGetters?.isPresenting)

  // A presentation refresh that lands MID-import must not touch the viewport or
  // declare the import over (AHA-46792).
  //
  // The clean-up below is an INFERENCE: "an import is running and the slides
  // just changed, therefore it finished". That held while the backend wrote the
  // whole deck in one go — one change meant done. Progressive import writes in
  // batches and then patches captions, so the inference fires on the first
  // batch, ~26s in: the toaster dies, "Your slides are ready!" pops, and the
  // success analytics fire while captioning still has ~55s to run.
  //
  // `importInFlight` is computed by the caller AT ENTRY, before any await — see
  // handleUpdatePresentationToSocketAction. Reading it here instead would read
  // it several awaits late, and two refreshes issued microseconds apart (the
  // last progress tick and the post-terminal one) would both observe the state
  // already cleared and both run the clean-up.

  // Re-activating a slide scrolls the editor to it. Doing that on every batch
  // yanks the viewport away from whatever the user is looking at.
  //
  // Unless the slide on screen is gone: the editable import into a fresh
  // presentation drops the blank first slide as soon as the first imported
  // slide lands (AHA-47757), and that blank is what the editor is showing.
  // Staying put would leave the canvas on a slide that no longer exists for
  // the rest of the job, with edits going to a deleted row.
  if ((!importInFlight && !holdsActiveSlide) || activeSlideGone) {
    // Handle slide activation and related side effects
    await setActiveSlideIfNeeded(dispatch, slideToActivate, rootGetters)

    // Perform DOM updates
    await performDOMUpdates()
  }

  // Turn off loading for importing slides
  if (importInFlight) return

  // The refresh has to be for the SAME deck that is importing.
  //
  // isCurrentPresentationImportingSlides answers "is the deck on screen
  // importing", which is not the same question. Imports outlive the page they
  // started from, so a long import on deck A finishing while the user has
  // moved on and started one on deck B would come through here with
  // presentationId=A, see deck B's flag set, and declare B finished — clearing
  // B's toaster and popping "Your slides are ready!" for an import still
  // minutes from done.
  const importingId = rootGetters['aiChatbotImportPptxPdf/importingSlidesForPresentationId']
  const generatingId = rootGetters['aiChatbotImportPptxPdf/generatingSlidesForPresentationId']
  const isThisDecksImport = importingId != null && importingId === presentationId
  const isThisDecksGeneration = generatingId != null && generatingId === presentationId

  if (isThisDecksImport || isThisDecksGeneration) {
    // AHA2-171: a user-CANCELLED import must never be reported as a success. The
    // import action's cancel branch (importSlidesAsyncAction, wasCancelled)
    // deliberately skips its own success emit but still runs THIS socket
    // clean-up — so without this guard the clean-up becomes the first (and only,
    // hence un-deduped) IMPORT_V2_SUCCESSFUL and pops "Your slides are ready!"
    // for a deck the user just cancelled. Pid-scoped because the cancelled
    // import need not be the deck on screen. The importCancel flag is still set
    // here: resetImportFlowStateAction (which the cancel branch dispatched) does
    // not clear it. Optional call so older call sites/tests without the getter
    // simply read as "not cancelled".
    const wasCancelled = Boolean(
      rootGetters['aiChatbotImportPptxPdf/wasImportCancelledFor']?.(presentationId),
    )

    // Track import success before clearing state
    // Identified, so this and importSlidesAsyncAction's own report cannot both
    // land for the same import — see trackImportSuccessful. This one stays
    // because it is the agent path's ONLY success signal.
    if (!wasCancelled) {
      trackImportSuccessBeforeClearingState(rootGetters, presentationId)
    }

    // Clear the flags either way — the import IS over (cancelled or finished);
    // leaving the importing flag set would hang the progress toaster. The
    // notification resolves to its Canceled state from the still-set cancel flag
    // when this clears the importing flag (its isImporting -> false watcher).
    dispatch('aiChatbotImportPptxPdf/clearImportingSlidesForPresentationIdAction', presentationId, { root: true })
    dispatch('aiChatbotImportPptxPdf/clearGeneratingSlidesForPresentationIdAction', presentationId, { root: true })
    dispatch('aiChatbotImportPptxPdf/setAIChatbotUploadedFileAction', null, { root: true })
    dispatch('aiChatbotImportPptxPdf/setSourceFileAction', null, { root: true })
    if (!wasCancelled) {
      // Stamped so a toaster mounted on another deck can ignore it.
      EventBus.$emit('import: show_import_status_success', { presentationId })
    }
  }
}

/**
 * Fetches updated presentation data from API
 * @param {string} presentationId - The presentation ID to fetch
 * @returns {Promise<Array>} Array of slides from API response
 */
const fetchUpdatedPresentationData = async (presentationId) => {
  const response = await PresentationApi.getPresentationDetails(presentationId)
  return response?.body?.Slides || []
}

/**
 * Gets the first annotated slide ID from stored annotation data
 * @param {Function} dispatch - Vuex dispatch function
 * @param {Function} rootGetters - Vuex root getters function
 * @param {boolean} canUseSlideAnnotations - Whether slide annotations feature is enabled
 * @returns {Promise<string|null>} First slide ID with annotation from socket event, or null
 */
const getFirstAnnotatedSlideId = async (dispatch, rootGetters, canUseSlideAnnotations) => {
  if (!canUseSlideAnnotations) {
    return null
  }

  // Use the stored first annotated slide ID from the annotationsUpdated socket event
  const firstAnnotatedSlideId = rootGetters['slideAnnotations/firstAnnotatedSlideId']

  // Clear the stored value after using it to prevent reuse in subsequent calls
  if (firstAnnotatedSlideId) {
    await dispatch('slideAnnotations/clearFirstAnnotatedSlideId', null, { root: true })
  }

  return firstAnnotatedSlideId
}

const actions = {
  /**
   * Handles socket events for presentation updates after third-party slide generation
   * Updates the presentation data and sets the active slide appropriately
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.dispatch - Vuex dispatch function
   * @param {Object} context.rootState - Root Vuex state
   */
  async handleUpdatePresentationToSocketAction({ dispatch, rootState, rootGetters }, payload = {}) {
    const { presentation } = rootState
    const originalSlides = JSON.parse(JSON.stringify(presentation?.slides || [])) // Deep copy to preserve original state
    const presentationId = payload.presentationId || presentation?.id

    // Early validation
    if (!validatePresentationId(presentationId)) {
      return
    }

    // Abort if a different presentation is loaded (user navigated away mid-import)
    if (presentation?.id && presentation.id !== presentationId) {
      return
    }

    // Read the import guard NOW, before the awaits below (AHA-46792).
    //
    // Two things depend on it being sampled here. The poll loop clears
    // importProgress the instant the job reaches a terminal state, and a
    // refresh dispatched just before that — the last progress tick — is still
    // in flight; reading after the awaits would see it cleared and run the
    // import-finished clean-up a second time, emitting a duplicate success
    // toaster and duplicate success analytics. On a FAILED import that same
    // race can pop "Your slides are ready!" over the error toaster.
    //
    // Scoped by presentationId because importProgress is module state and one
    // import's poll can outlive the user's presence on that deck: without the
    // comparison, navigating away mid-import would suppress slide activation on
    // the NEXT presentation for the remainder of the poll.
    // The MAP, keyed by the deck this refresh is for — not the `importProgress`
    // getter, which resolves against the deck currently on screen.
    //
    // Those differ exactly when no editor is mounted, and the poll loop
    // explicitly supports that: an import started from the my-presentations
    // list runs with presentation.id === null. The getter returns null there,
    // so importInFlight would be false and every progressive tick would run the
    // finished clean-up — clearing the importing flag and firing the success
    // toaster and analytics while the import is still going.
    const progressByPresentation = rootGetters['aiChatbotImportPptxPdf/importProgressByPresentation']
    const importProgress = progressByPresentation?.[presentationId]
    const importInFlight = Boolean(importProgress)
      && importProgress.presentationId === presentationId

    try {
      // Step 1: Fetch updated presentation data
      const slidesFromApi = await fetchUpdatedPresentationData(presentationId)

      // Step 2: Process slides to fetch canvas blocks
      const processedSlides = await processCanvasBlocksForSlides(slidesFromApi)

      // Step 3: Update presentation state with processed slides
      await updatePresentationState(dispatch, rootState, processedSlides)

      // Step 4: Get first annotated slide ID from stored annotation data
      const canUseSlideAnnotations = rootGetters['slideAnnotations/canUseSlideAnnotations']
      const firstSlideIdWithAnnotation = await getFirstAnnotatedSlideId(
        dispatch,
        rootGetters,
        canUseSlideAnnotations,
      )

      // Step 5: Determine and set the appropriate active slide
      const slideToActivate = determineActiveSlide(
        originalSlides,
        processedSlides,
        firstSlideIdWithAnnotation,
      )

      // Step 6: Handle UI updates
      const activeSlideId = rootState.slideActive?.id
      const activeSlideGone = activeSlideId != null
        && !processedSlides.some((slide) => slide.id === activeSlideId)
      await handleUIUpdates(
        dispatch, slideToActivate, rootGetters, importInFlight, presentationId, activeSlideGone,
      )

      // Step 7: Track newly created slides for analytics (done at the end to avoid disrupting main flow)
      const newSlides = identifyNewSlides(originalSlides, processedSlides)

      if (newSlides.length > 0) {
        trackChatbotSlideCreation(newSlides, presentation)
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  },
}

export default {
  namespaced: true,
  actions,
}
